import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useBaasCountryOptions } from "../../../../../Hooks/useBaasCountryOptions";
import {
  deleteBaasEntityDocument,
  fetchBaasEntityDocumentContent,
  updateBaasEntityDocument,
  uploadBaasEntityDocuments,
  upsertBaasEntityDocumentDeclaration,
} from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceFeatures } from "../../../../../Services/baasInstancesApi";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import {
  extractDocumentCountryCode,
  resolveIdentityDocumentLabelVariant,
} from "../baasIdentityDocumentLabels";
import {
  normalizeSchemeCountry,
  resolveNationalIdScheme,
} from "../baasIdentitySchemes";
import {
  buildExistingProofByType,
  emptyDeclarationDoc,
  extractRawBase64,
  fetchUrlAsBase64,
  formatDocumentTypeLabel,
  formatStoredFileLabel,
  getDocumentTypeKey,
  hasForeignDriversLicenseIdentity,
  hasUploadedContent,
  isBrCpfProofDeclaration,
  isDeclarationDocument,
  readFileAsBase64,
  resolveUploadContentType,
  toPreviewSrc,
} from "../baasEntidadeDocumentUtils";
import { BaasEntidadeDocumentPreviewModal } from "./BaasEntidadeDocumentPreviewModal";
import { BaasEntidadeVerificationsPanel } from "./BaasEntidadeVerificationsPanel";

const KYC_LEP_FEATURE = "kyc.legal_entity_proposal_images";

export const BaasEntidadeDocumentsPanel = ({
  documents,
  instance,
  entityId,
  entityType,
  uploadEnabled,
  canEditMeta,
  onUploaded,
  onDocumentUpdated,
  embedded = false,
  verificationEntity = null,
  allowSkipKyc = null,
  allowPaySmartImageSync = false,
}) => {
  const { countryOptions } = useBaasCountryOptions(instance);
  const [lepFeatureEnabled, setLepFeatureEnabled] = useState(false);
  const [preview, setPreview] = useState({
    open: false,
    title: "",
    src: null,
    contentType: null,
    loading: false,
    error: "",
    base64: null,
    base64Loading: false,
  });
  const [savingId, setSavingId] = useState(null);
  const [saveErrors, setSaveErrors] = useState({});
  const [saveSuccesses, setSaveSuccesses] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [checklistUploadingType, setChecklistUploadingType] = useState("");
  const [checklistUploadError, setChecklistUploadError] = useState("");
  const [checklistUploadSuccess, setChecklistUploadSuccess] = useState("");
  const [pendingDeclarationSlots, setPendingDeclarationSlots] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (entityType !== "pj" || !instance?.baasInstanceId) {
      setLepFeatureEnabled(false);
      return undefined;
    }

    (async () => {
      try {
        const features = await fetchBaasInstanceFeatures(instance);
        if (cancelled) return;
        const enabled = features.some(
          (item) =>
            String(item.featureCode || "").toLowerCase() === KYC_LEP_FEATURE
            && item.enabled === true,
        );
        setLepFeatureEnabled(enabled);
      } catch {
        if (!cancelled) setLepFeatureEnabled(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entityType, instance]);

  const declarationDocs = useMemo(
    () => (Array.isArray(documents) ? documents.filter(isDeclarationDocument) : []),
    [documents],
  );

  const uploadedDocs = useMemo(() => {
    if (!Array.isArray(documents) || documents.length === 0) return [];
    return documents.filter(hasUploadedContent);
  }, [documents]);

  const existingProofByType = useMemo(
    () => buildExistingProofByType(uploadedDocs),
    [uploadedDocs],
  );

  const nationalIdScheme = useMemo(() => {
    const dl = declarationDocs.find(
      (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE",
    );
    const nid = declarationDocs.find(
      (item) => getDocumentTypeKey(item) === "NATIONAL_ID",
    );
    const country =
      extractDocumentCountryCode(nid)
      || extractDocumentCountryCode(dl)
      || null;
    return resolveNationalIdScheme(country);
  }, [declarationDocs]);

  const closePreview = () => {
    setPreview({
      open: false,
      title: "",
      src: null,
      contentType: null,
      loading: false,
      error: "",
      base64: null,
      base64Loading: false,
    });
  };

  const resolveBase64FromUrl = async (url) => {
    if (!url) return;
    setPreview((current) =>
      current.open ? { ...current, base64Loading: true } : current,
    );
    try {
      const raw = await fetchUrlAsBase64(url);
      setPreview((current) =>
        current.open ? { ...current, base64: raw, base64Loading: false } : current,
      );
    } catch {
      setPreview((current) =>
        current.open ? { ...current, base64Loading: false } : current,
      );
    }
  };

  const handleViewDocument = async (doc) => {
    const documentType = doc.document_id || doc.documentId || doc.description;
    const docCountry = extractDocumentCountryCode(doc);
    const title = formatDocumentTypeLabel(documentType, {
      doc,
      countryCode: docCountry,
      countryOptions,
      variant: resolveIdentityDocumentLabelVariant({
        documentType,
        countryCode: docCountry,
        isLinkedNationalIdProof: Boolean(
          doc.related_person_document_id || doc.relatedPersonDocumentId,
        ),
      }),
    });
    const listType = doc.content_type || doc.contentType || doc.mime_type || doc.mimeType || null;

    setPreview({
      open: true,
      title,
      src: null,
      contentType: listType,
      loading: true,
      error: "",
      base64: null,
      base64Loading: false,
    });

    if (!hasUploadedContent(doc)) {
      setPreview((current) => ({
        ...current,
        loading: false,
        error: intl.get("baas.entidades.detail.viewDocumentUnavailable"),
      }));
      return;
    }

    if (instance && entityId && doc?.id) {
      try {
        const content = await fetchBaasEntityDocumentContent(instance, entityId, doc.id);
        const nextBase64 =
          extractRawBase64(content?.file_base64 || content?.fileBase64 || null) || null;
        const nextType =
          content?.content_type || content?.contentType || listType || null;
        const nextLink = nextBase64 ? null : content?.link || content?.Link || null;
        const src = toPreviewSrc(nextBase64, nextType) || nextLink;
        if (!src) {
          setPreview((current) => ({
            ...current,
            loading: false,
            base64Loading: false,
            error: intl.get("baas.entidades.detail.viewDocumentUnavailable"),
          }));
          return;
        }
        setPreview((current) => ({
          ...current,
          src,
          contentType: nextType,
          loading: false,
          error: "",
          base64: nextBase64,
          base64Loading: Boolean(nextLink) && !nextBase64,
        }));
        if (nextLink && !nextBase64) {
          await resolveBase64FromUrl(nextLink);
        }
        return;
      } catch (error) {
        const code = error?.code || error?.data?.error;
        if (code === "document_content_not_found") {
          setPreview((current) => ({
            ...current,
            loading: false,
            base64Loading: false,
            error: intl.get("baas.entidades.detail.viewDocumentUnavailable"),
          }));
          return;
        }
        setPreview((current) => ({
          ...current,
          loading: false,
          base64Loading: false,
          error: intl.get("baas.entidades.detail.viewDocumentError"),
        }));
        return;
      }
    }

    setPreview((current) => ({
      ...current,
      loading: false,
      base64Loading: false,
      error: intl.get("baas.entidades.detail.viewDocumentUnavailable"),
    }));
  };

  const handleSaveMeta = async (doc, patch) => {
    if (!canEditMeta || !instance || !entityId) return false;

    const documentType = String(
      doc.document_id || doc.documentId || doc.description || "",
    ).toUpperCase();
    const identityVariant = patch?.identityVariant || null;
    const existingId = String(doc.id || "").trim();
    const saveKey = existingId || documentType || "new";
    const proofFiles = Array.isArray(patch?.proofFiles) ? patch.proofFiles : [];
    const isBrCpfProofSave = isBrCpfProofDeclaration(
      documentType,
      identityVariant,
      patch.countryCode || doc.country_code || doc.countryCode,
    );
    const foreignDlBlocksCpfProof = isBrCpfProofSave
      && hasForeignDriversLicenseIdentity(declarationDocs, uploadedDocs);

    if (foreignDlBlocksCpfProof && proofFiles.length > 0) {
      setSaveErrors((current) => ({
        ...current,
        [saveKey]: intl.get("baas.entidades.detail.brCpfForeignDlProofConflict"),
      }));
      return false;
    }

    setSavingId(saveKey);
    setSaveErrors((current) => ({ ...current, [saveKey]: "" }));
    setSaveSuccesses((current) => ({ ...current, [saveKey]: "" }));

    try {
      const metaPatch = {
        number: patch.number,
        countryCode: patch.countryCode,
        issuingRegion: patch.issuingRegion,
        expirationDate: patch.expirationDate,
      };
      const updated = existingId
        ? await updateBaasEntityDocument(instance, entityId, existingId, metaPatch)
        : await upsertBaasEntityDocumentDeclaration(instance, entityId, {
            type: documentType,
            ...metaPatch,
          });

      if (proofFiles.length > 0) {
        if (!uploadEnabled) {
          throw Object.assign(new Error("upload_not_allowed"), {
            code: "documents_upload_not_allowed",
          });
        }
        const countryCode = normalizeSchemeCountry(patch.countryCode);
        const documentsPayload = await Promise.all(
          proofFiles.map(async (item) => {
            const fileBase64 = await readFileAsBase64(item.file);
            const type = String(item.type || "").toUpperCase();
            const payload = {
              type,
              content_type: resolveUploadContentType(item.file),
              file_base64: fileBase64,
              file_name: item.file.name,
            };
            if (
              isBrCpfProofSave
              && (type === "DRIVERS_LICENSE" || type === "DRIVERS_LICENSE_BACK")
            ) {
              payload.country_code = "BR";
            } else if (countryCode) {
              payload.country_code = countryCode;
            } else if (
              (type === "DRIVERS_LICENSE" || type === "DRIVERS_LICENSE_BACK")
              && nationalIdScheme.proofKind === "drivers_license"
            ) {
              payload.country_code = "BR";
            }
            return payload;
          }),
        );
        await uploadBaasEntityDocuments(instance, entityId, documentsPayload);
        onUploaded?.();
      } else {
        onDocumentUpdated?.(updated);
      }

      setPendingDeclarationSlots((current) =>
        current.filter((slot) => slot.type !== documentType),
      );
      setSaveSuccesses((current) => ({
        ...current,
        [saveKey]: intl.get("baas.entidades.detail.saveDeclarationAndProofsSuccess"),
      }));
      return true;
    } catch (error) {
      const code = error?.code || error?.data?.error;
      const detail = String(error?.detail || error?.message || "").trim();
      const brCpfDlConflict =
        detail.includes("Brazilian National ID (CPF) proof requires")
        || detail.includes("Comprovação de National ID (CPF) exige");
      const issuingLocked =
        code === "entity_validation_failed"
        || Boolean(error?.data?.errors?.issuing_region || error?.errors?.issuing_region);
      setSaveErrors((current) => ({
        ...current,
        [saveKey]:
          code === "documents_upload_not_allowed"
            ? intl.get("baas.entidades.detail.uploadNotAllowed")
            : code === "documents_selfie_and_id_required"
              ? intl.get("baas.entidades.detail.uploadSelfieAndIdRequired")
              : brCpfDlConflict
                ? intl.get("baas.entidades.detail.brCpfForeignDlProofConflict")
              : code === "validation_error" && detail
                ? detail
                : issuingLocked
                  ? intl.get("baas.entidades.detail.issuingRegionLockedHint")
                  : intl.get("baas.entidades.detail.saveDeclarationAndProofsError"),
      }));
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteProof = async (doc) => {
    if (!uploadEnabled || !instance || !entityId || !doc?.id) return;
    const documentId = doc.id;
    setDeletingId(documentId);
    setDeleteError("");
    setDeleteSuccess("");
    try {
      await deleteBaasEntityDocument(instance, entityId, documentId);
      setDeleteSuccess(intl.get("baas.entidades.detail.deleteDocumentSuccess"));
      onUploaded?.();
    } catch {
      setDeleteError(intl.get("baas.entidades.detail.deleteDocumentError"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCompanyChecklistUpload = async (documentType, file) => {
    if (!uploadEnabled || !instance || !entityId || !file || !documentType) return;
    const type = String(documentType).toUpperCase();
    setChecklistUploadingType(type);
    setChecklistUploadError("");
    setChecklistUploadSuccess("");
    try {
      const fileBase64 = await readFileAsBase64(file);
      await uploadBaasEntityDocuments(instance, entityId, [
        {
          type,
          content_type: resolveUploadContentType(file),
          file_base64: fileBase64,
          file_name: file.name,
        },
      ]);
      setChecklistUploadSuccess(
        intl.get("baas.entidades.detail.businessChecklistUploadSuccess", {
          type: formatDocumentTypeLabel(type),
        }),
      );
      onUploaded?.();
    } catch (error) {
      const code = error?.code || error?.data?.error;
      const detail = String(error?.detail || error?.message || "").trim();
      setChecklistUploadError(
        code === "documents_upload_not_allowed"
          ? intl.get("baas.entidades.detail.uploadNotAllowed")
          : code === "entity_validation_failed" || code === "validation_error"
            ? formatBaasConsoleConflictMessage(error)
              || detail
              || intl.get("baas.entidades.detail.uploadError")
            : intl.get("baas.entidades.detail.uploadError"),
      );
    } finally {
      setChecklistUploadingType("");
    }
  };

  const handleAddDeclarationSlot = ({ countryCode, documentType, identityVariant = null }) => {
    const type = String(documentType || "").toUpperCase();
    const cc = normalizeSchemeCountry(countryCode);
    setPendingDeclarationSlots((current) => {
      if (current.some((slot) => {
        if (slot.type !== type) return false;
        if (type === "NATIONAL_ID") {
          return slot.identityVariant === identityVariant;
        }
        return true;
      })) return current;
      return [
        ...current,
        {
          type,
          identityVariant: identityVariant || null,
          doc: emptyDeclarationDoc(type, cc),
          forcedCountryCode: cc,
        },
      ];
    });
  };

  return (
    <div className={embedded ? "mt-6 border-t border-gray-100 pt-6 dark:border-brand-dark-border" : "mt-5"}>
      {embedded ? (
        <BaasEntidadeVerificationsPanel
          embedded
          entity={verificationEntity}
          documents={Array.isArray(documents) ? documents : []}
          entityType={entityType}
          uploadEnabled={uploadEnabled}
          canEditMeta={canEditMeta}
          countryOptions={countryOptions}
          declarationDocs={declarationDocs}
          uploadedDocs={uploadedDocs}
          existingProofByType={existingProofByType}
          pendingDeclarationSlots={pendingDeclarationSlots}
          onAddDeclarationSlot={handleAddDeclarationSlot}
          onViewDocument={handleViewDocument}
          onSaveDeclaration={handleSaveMeta}
          onDeleteDocument={handleDeleteProof}
          onCompanyChecklistUpload={handleCompanyChecklistUpload}
          savingId={savingId}
          saveErrors={saveErrors}
          saveSuccesses={saveSuccesses}
          deletingId={deletingId}
          deleteError={deleteError}
          deleteSuccess={deleteSuccess}
          checklistUploadingType={checklistUploadingType}
          checklistUploadError={checklistUploadError}
          checklistUploadSuccess={checklistUploadSuccess}
          lepFeatureEnabled={lepFeatureEnabled}
          instance={instance}
          entityId={entityId}
          onSynced={onUploaded}
          allowSkipKyc={allowSkipKyc ?? uploadEnabled}
          allowPaySmartImageSync={allowPaySmartImageSync}
          formatStoredFileLabel={(doc) =>
            formatStoredFileLabel(doc, countryOptions, documents)
          }
        />
      ) : (
        <div className="app-card p-5">
          <BaasEntidadeVerificationsPanel
            entity={verificationEntity}
            documents={Array.isArray(documents) ? documents : []}
            entityType={entityType}
            uploadEnabled={uploadEnabled}
            canEditMeta={canEditMeta}
            countryOptions={countryOptions}
            declarationDocs={declarationDocs}
            uploadedDocs={uploadedDocs}
            existingProofByType={existingProofByType}
            pendingDeclarationSlots={pendingDeclarationSlots}
            onAddDeclarationSlot={handleAddDeclarationSlot}
            onViewDocument={handleViewDocument}
            onSaveDeclaration={handleSaveMeta}
            onDeleteDocument={handleDeleteProof}
            onCompanyChecklistUpload={handleCompanyChecklistUpload}
            savingId={savingId}
            saveErrors={saveErrors}
            saveSuccesses={saveSuccesses}
            deletingId={deletingId}
            deleteError={deleteError}
            deleteSuccess={deleteSuccess}
            checklistUploadingType={checklistUploadingType}
            checklistUploadError={checklistUploadError}
            checklistUploadSuccess={checklistUploadSuccess}
            lepFeatureEnabled={lepFeatureEnabled}
            instance={instance}
            entityId={entityId}
            onSynced={onUploaded}
            allowSkipKyc={allowSkipKyc ?? uploadEnabled}
            allowPaySmartImageSync={allowPaySmartImageSync}
            formatStoredFileLabel={(doc) =>
            formatStoredFileLabel(doc, countryOptions, documents)
          }
          />
        </div>
      )}

      <BaasEntidadeDocumentPreviewModal
        open={preview.open}
        title={preview.title}
        src={preview.src}
        contentType={preview.contentType}
        loading={preview.loading}
        error={preview.error}
        base64={preview.base64}
        base64Loading={preview.base64Loading}
        onClose={closePreview}
      />
    </div>
  );
};

export default BaasEntidadeDocumentsPanel;
